import React, { useEffect, useState } from 'react';
import { Menu, X, ChevronDown, ArrowRight, MessageCircle } from 'lucide-react';
import { navLinks, whatsappLink } from '../mock';
import Logo from './Logo';
import { useServiceEnquiry } from './ServiceEnquiry';

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileServices, setMobileServices] = useState(false);
  const { openService } = useServiceEnquiry();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const go = (href) => {
    setMobileOpen(false);
    setMobileServices(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled || mobileOpen ? 'bg-[#f7f6f2]/95 backdrop-blur-xl border-b border-black/5 shadow-[0_8px_30px_-20px_rgba(20,20,15,0.35)]' : 'bg-transparent'}`}>
      <div className="section-pad max-w-[1440px] mx-auto flex items-center justify-between h-16 sm:h-[76px]">
        <button onClick={() => go('#home')} className="flex items-center select-none shrink-0" aria-label="VINYLED home">
          <Logo className="h-6 sm:h-8" />
        </button>

        <nav className="hidden lg:flex items-center gap-1">
          {navLinks.map((link) => (
            <div key={link.label} className="relative group">
              <button onClick={() => go(link.href)} className="flex items-center gap-1 px-4 py-2 text-[0.9rem] font-medium text-[#4a463d] hover:text-[#16150f] transition-colors duration-300">
                {link.label}
                {link.groups && <ChevronDown className="w-3.5 h-3.5 opacity-50 group-hover:rotate-180 transition-transform duration-300" />}
              </button>
              {link.groups && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 pt-2 opacity-0 invisible translate-y-2 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-300 z-50">
                  <div className="card-elevated rounded-2xl p-4 w-[560px] grid grid-cols-3 gap-2">
                    {link.groups.map((group) => (
                      <div key={group.title} className="px-2 py-1">
                        <p className="text-[0.62rem] font-semibold tracking-widest text-[#8a8578] mb-2">{group.index} · {group.title.toUpperCase()}</p>
                        {group.items.map((item) => (
                          <button key={item} onClick={() => openService(item, { category: group.title })} className="w-full text-left px-0 py-1.5 text-sm text-[#4a463d] hover:text-[#ff5a1f] transition-colors">
                            {item}
                          </button>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <a href={whatsappLink()} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-black/10 text-[#16150f] hover:bg-[#25D366] hover:text-white hover:border-[#25D366] transition-all" aria-label="WhatsApp">
            <MessageCircle className="w-4 h-4" />
          </a>
          <button onClick={() => go('#contact')} className="hidden sm:inline-flex items-center gap-2 pl-5 pr-2 py-2 rounded-full text-sm font-semibold text-white bg-[#16150f] hover:bg-[#ff5a1f] transition-all duration-300 group">
            Book Consultation
            <span className="w-7 h-7 rounded-full bg-[#ff5a1f] group-hover:bg-white flex items-center justify-center transition-colors duration-300">
              <ArrowRight className="w-3.5 h-3.5 text-white group-hover:text-[#16150f] -rotate-45 group-hover:rotate-0 transition-transform duration-300" />
            </span>
          </button>
          <button onClick={() => setMobileOpen(!mobileOpen)} className="lg:hidden text-[#16150f] p-2 -mr-1" aria-label="Menu">
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden bg-[#f7f6f2] border-t border-black/5 px-5 py-4 h-[calc(100dvh-4rem)] overflow-y-auto">
          {navLinks.map((link) => (
            <div key={link.label}>
              {link.groups ? (
                <>
                  <button onClick={() => setMobileServices((v) => !v)} className="flex w-full items-center justify-between py-3.5 text-[#16150f] border-b border-black/5">
                    {link.label}
                    <ChevronDown className={`w-4 h-4 transition-transform ${mobileServices ? 'rotate-180' : ''}`} />
                  </button>
                  {mobileServices && (
                    <div className="pb-3 border-b border-black/5">
                      {link.groups.map((group) => (
                        <div key={group.title} className="pt-3">
                          <p className="text-[0.62rem] font-semibold tracking-widest text-[#8a8578] mb-1">{group.index} · {group.title.toUpperCase()}</p>
                          {group.items.map((item) => (
                            <button key={item} onClick={() => { openService(item, { category: group.title }); setMobileOpen(false); }} className="block w-full text-left py-2 text-sm text-[#5a5548]">
                              {item}
                            </button>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <button onClick={() => go(link.href)} className="block w-full text-left py-3.5 text-[#16150f] border-b border-black/5">
                  {link.label}
                </button>
              )}
            </div>
          ))}
          <a href={whatsappLink()} target="_blank" rel="noreferrer" className="mt-5 w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full text-sm font-semibold text-white bg-[#25D366]">
            <MessageCircle className="w-4 h-4" /> Chat on WhatsApp
          </a>
          <button onClick={() => go('#contact')} className="mt-3 w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full text-sm font-semibold text-white bg-[#16150f]">
            Book Consultation <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </header>
  );
}
